var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import { isInternal } from '../../utils/is-internal';
import { usePromoClickEvent } from '../../hooks/use-promo-click-event';
import { FULLWIDTH_NAVIGATION_NAME } from '../../constants/analyticsNames';
import { FULLWIDTH_NAVIGATION } from '../../constants/cmsResourceTypes';
import PromoImagery from '../../components/PromoImagery';
import AnchorLink from '../../components/AnchorLink';
import SmallText from '../../components/SmallText/SmallText';
import PromoText from './PromoText/PromoText';
import styles from './FullWidthNavigation.module.scss';
function throttle(func, timeFrame) {
    let lastTime = 0;
    return (...args) => {
        const now = new Date();
        if (now - lastTime >= timeFrame) {
            func(...args);
            lastTime = now;
        }
    };
}
const DARK_THEME = 'dark';
const LIGHT_THEME = 'light';
const FullWidthNavigation = (_a) => {
    var _b;
    var { analyticsTags, cqResponsiveClasses, taxonomyLevel, position, preloadImages } = _a, data = __rest(_a, ["analyticsTags", "cqResponsiveClasses", "taxonomyLevel", "position", "preloadImages"]);
    const promoClickEvent = usePromoClickEvent();
    const imageRef = useRef(null);
    const userPrefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const { image = {}, styleIds = [], textArea = {
        textCentered: false,
        buttonCentered: false,
        theme: DARK_THEME,
    }, icons, } = data !== null && data !== void 0 ? data : {};
    const { parallaxEffect = false, imagePosition } = image;
    const { backgroundColor, buttonCentered, callToActionAltText, callToActionText, callToActionURL, disclaimer, newWindow, textCentered, theme, } = textArea;
    const isParallaxEffectEnabled = parallaxEffect && !userPrefersReducedMotion;
    const parallaxSpeed = 0.3;
    const parallaxOffsetMultiplier = 0.2;
    const handleScroll = throttle(() => {
        if (!imageRef.current)
            return;
        const rect = imageRef.current.getBoundingClientRect();
        const newParallaxOffset = rect.top * parallaxOffsetMultiplier;
        imageRef.current.style.transform = `translateY(${newParallaxOffset * parallaxSpeed}px)`;
        imageRef.current.style.transition = 'transform 0.1s linear';
    }, 50);
    useEffect(() => {
        if (typeof window !== 'undefined' && isParallaxEffectEnabled) {
            const rect = imageRef.current.getBoundingClientRect();
            const newParallaxOffset = rect.top * parallaxOffsetMultiplier;
            imageRef.current.parentElement.style.height = `${rect.height + newParallaxOffset * 2}px`;
            imageRef.current.parentElement.style.marginTop = `${-newParallaxOffset}px`;
            window.addEventListener('scroll', handleScroll);
        }
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isParallaxEffectEnabled]);
    if (!Object.keys(image).length)
        return null;
    const is5050Layout = styleIds.includes('50/50-layout');
    const aemAuthoredBackground = backgroundColor;
    const iconMessage = icons.messageIcon.display && icons.messageIcon.data.message;
    const imageWrapperStyles = ['col-xs-12of12', 'col-sm-12of12', styles.imageBoxWrapper];
    const textWrapperStyles = ['col-xs-12of12', 'col-sm-12of12', styles.textBoxWrapper];
    if (is5050Layout) {
        imageWrapperStyles.push('col-md-6of12', 'col-lg-6of12', 'col-xl-6of12');
        textWrapperStyles.push('col-md-6of12', 'col-lg-6of12', 'col-xl-6of12');
    }
    else {
        imageWrapperStyles.push('col-md-7of12', 'col-lg-7of12', 'col-xl-7of12');
        textWrapperStyles.push('col-md-5of12', 'col-lg-5of12', 'col-xl-5of12');
    }
    const imageBox = (React.createElement("div", { className: imageWrapperStyles.join(' '), "data-testid": "full-width-navigation-image-box" },
        React.createElement("div", { className: styles.imageBox },
            React.createElement(PromoImagery, { icons: data.icons, image: data.image, hideMessage: true, imageRef: imageRef, lazyLoad: !preloadImages }))));
    const textBox = (React.createElement("div", { className: textWrapperStyles.join(' '), "data-testid": "full-width-navigation-text-box", style: aemAuthoredBackground ? { backgroundColor } : {} },
        React.createElement("div", { className: classNames(styles.textBox, {
                [styles.textCentered]: textCentered,
            }), "data-testid": "full-width-navigation-text-box-inner" },
            iconMessage && (React.createElement("div", { className: styles.messageWrapper },
                React.createElement("span", { className: classNames(styles.message, {
                        [styles.lightThemeText]: theme === LIGHT_THEME,
                    }) }, iconMessage))),
            data.textArea && (React.createElement("div", { className: styles.textWrapper },
                React.createElement(PromoText, { data: Object.assign(Object.assign({}, data.textArea), { theme }), wrappedByLink: !!callToActionURL })))),
        !!callToActionURL && (React.createElement("div", { className: classNames(styles.ctaWrapper, {
                [styles.buttonCentered]: buttonCentered,
            }), "data-testid": "full-width-navigation-cta" },
            React.createElement(Button, { component: "div", label: callToActionText, theme: theme === LIGHT_THEME ? 'secondaryLight' : 'secondary', type: undefined, "data-testid": "cta-button" }))),
        !!((_b = data === null || data === void 0 ? void 0 : data.textArea) === null || _b === void 0 ? void 0 : _b.displayDisclaimer) && (React.createElement("div", { className: classNames(styles.textBox, {
                [styles.textCentered]: textCentered,
            }), "data-testid": "disclaimer" },
            React.createElement(SmallText, { text: disclaimer !== null && disclaimer !== void 0 ? disclaimer : '', classes: classNames(styles.disclaimer, {
                    [styles.lightThemeText]: theme === LIGHT_THEME,
                }) })))));
    let inner = (React.createElement("div", { className: classNames(styles.inner, {
            [styles.imageOnLeft]: imagePosition === 'left',
        }), "data-testid": "full-width-navigation-inner" },
        imageBox,
        textBox));
    if (callToActionURL) {
        const linkProps = {
            'aria-label': callToActionAltText,
            href: callToActionURL,
            onClick: () => {
                var _a, _b, _c;
                promoClickEvent(Object.assign(Object.assign({}, data.analytics), { creative: FULLWIDTH_NAVIGATION_NAME, id: data.componentId, name: (_a = data.analytics) === null || _a === void 0 ? void 0 : _a.promoName, position, imageUrl: (_c = (_b = data.image) === null || _b === void 0 ? void 0 : _b.landscapeImage) === null || _c === void 0 ? void 0 : _c.fileReference, linkUrl: callToActionURL, newWindow }), taxonomyLevel);
            },
            newWindow,
        };
        if (newWindow && !isInternal(callToActionURL))
            linkProps.rel = 'noopener';
        inner = (React.createElement(AnchorLink, Object.assign({}, linkProps, { className: classNames(styles.inner, {
                [styles.imageOnLeft]: imagePosition === 'left',
            }), "data-testid": "full-width-navigation-inner" }),
            imageBox,
            textBox));
    }
    return (React.createElement("div", Object.assign({ className: classNames(styles.wrapper, cqResponsiveClasses) }, analyticsTags), inner));
};
FullWidthNavigation.displayName = FULLWIDTH_NAVIGATION_NAME;
FullWidthNavigation.resourceType = FULLWIDTH_NAVIGATION;
FullWidthNavigation.propTypes = {
    analyticsTags: PropTypes.any,
    cqResponsiveClasses: PropTypes.any,
    taxonomyLevel: PropTypes.any,
    position: PropTypes.string.isRequired,
    preloadImages: PropTypes.bool,
    image: PropTypes.shape({
        parallaxEffect: PropTypes.bool,
        imagePosition: PropTypes.string,
    }),
    styleIds: PropTypes.arrayOf(PropTypes.string),
};
FullWidthNavigation.defaultProps = {
    analyticsTags: {},
    cqResponsiveClasses: '',
    image: {},
    preloadImages: false,
    styleIds: [],
    taxonomyLevel: '',
};
export default FullWidthNavigation;
//# sourceMappingURL=index.js.map